// src/data/errors.js
//
// Typed error classes used by every src/data/*Client.js. The contract
// (src/data/CONTRACT.md) names exactly these classes; the React layer
// can `instanceof`-discriminate when it wants nuanced behavior, or just
// catch `Error` and surface a generic banner.
//
// Mapping is keyed off HTTP status:
//   network failure -> NetworkError
//   400             -> ValidationError   (server's "kind" hint preserved)
//   401, 403        -> AuthError
//   404             -> NotFoundError
//   409             -> ConflictError
//   anything else   -> Error              (generic; surfaced by the banner)
//
// The server already shapes its errors as
// `{ error: string, kind?: string, details?: object }` (see server/lib/errors.js),
// so we just promote that envelope into the JS class hierarchy here.

export class NetworkError extends Error {
  constructor(message, cause) {
    super(message || "Network request failed");
    this.name = "NetworkError";
    this.cause = cause;
  }
}

export class AuthError extends Error {
  constructor(message, status) {
    super(message || "Not authorized");
    this.name = "AuthError";
    this.status = status;
  }
}

export class ValidationError extends Error {
  constructor(message, details) {
    super(message || "Invalid request");
    this.name = "ValidationError";
    this.details = details || null;
  }
}

export class NotFoundError extends Error {
  constructor(message) {
    super(message || "Not found");
    this.name = "NotFoundError";
  }
}

export class ConflictError extends Error {
  constructor(message, details) {
    super(message || "Conflict");
    this.name = "ConflictError";
    this.details = details || null;
  }
}

/**
 * Normalize a fetch Response into a JS error of the right class.
 * Reads the JSON envelope when present, falls back to status text.
 *
 * Caller is expected to have already verified !res.ok.
 */
export async function errorFromResponse(res) {
  let body = null;
  try { body = await res.json(); } catch { /* not JSON; ignore */ }
  const msg = body?.error || res.statusText || `HTTP ${res.status}`;
  const details = body?.details;
  switch (res.status) {
    case 400: return new ValidationError(msg, details);
    case 401:
    case 403: return new AuthError(msg, res.status);
    case 404: return new NotFoundError(msg);
    case 409: return new ConflictError(msg, details);
    default:  return new Error(msg);
  }
}
